import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  TextField,
  Box,
  Button,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RemoveIcon from '@mui/icons-material/Remove';
import { useVirtualizer } from '@tanstack/react-virtual';
import './MultiSelectCheckbox.css';
import { SxProps, Theme } from '@mui/material/styles';

export interface Option {
  id: string;
  value: string;
  displayValue?: string;
  groupLabel?: string;

  // その選択肢が属している課ID（department_id）
  //   例: "1", "2", "10" ...
  //   チームや記入者や週報分類のマスタ側で入れておく
  department_id?: string;

  // 週報分類など、選択肢に色が設定されている場合のスウォッチ表示用（未指定なら非表示）
  color?: string | null;
}

interface MultiSelectCheckboxProps {
  label: React.ReactNode;
  options: Option[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  onOpen?: () => void;
  itemHeight?: number;
  maxVisibleItems?: number;
  // 大量候補向け。検索対象は全件のまま、メニューに描画する件数だけを制限する。
  maxRenderedOptions?: number;
  // 入力検索時の描画上限。未指定時は従来どおり100件。
  maxSearchRenderedOptions?: number;
  initialRenderedOptions?: number;
  renderedOptionsStep?: number;
  // 初回件数を抑え、全候補の取得完了後は上限を解除して全件表示する。
  renderAllOptionsWhenLoaded?: boolean;
  // 取得中から自動的に描画を増やす場合の最終DOM上限。
  maxAutoRenderedOptions?: number;
  loadedOptionCount?: number;
  allOptionsLoaded?: boolean;
  // 指定した場合、false の間は件数表示・全選択・候補行を描画しない。
  // true へ変わった後も、最終表示件数の準備が整ってからまとめて描画する。
  optionDisplayReady?: boolean;
  remoteOptions?: Option[];
  onFilterTextChange?: (value: string) => void;
  searchLoading?: boolean;
  searchResultsComplete?: boolean;
  searchError?: string | null;
  // 候補の一覧取得そのものが失敗した場合の案内。空一覧(0名確定)と区別して表示する。
  loadErrorMessage?: string | null;
  // true の間、候補の取得が完了した後に「選択中だが候補に無いID」があれば案内する。
  notifyUnlistedSelected?: boolean;
  // 候補に無くても案内の対象外にするID（本人など、残す選択）。大文字小文字・前後空白は無視。
  unlistedSelectedIgnoreIds?: string[];
  // 指定すると、案内に「候補に無い選択を解除」ボタンを表示する。
  onClearUnlistedSelected?: () => void;
  searchLoadingText?: string;
  // 検索中の状態制御は維持したまま、短時間だけ表示される案内文を非表示にできる。
  showSearchLoadingMessage?: boolean;
  virtualizeOptions?: boolean;
  // 重い画面でもメニューのopacity/scaleアニメーションによる点滅を発生させない。
  disableMenuTransition?: boolean;
  // 仮想行を推定値から実測値へ変更せず、初回描画から同じ位置へ固定する。
  fixedVirtualRowHeight?: boolean;
  countUnit?: string;
  loading?: boolean;
  loadingText?: string;
  showLoadingMessage?: boolean;
  labelSx?: SxProps<Theme>;

  // 表示させたい課IDの配列
  //   ["1"] のように渡すと department_id === "1" のものだけ表示
  //   未指定 or 空ならフィルターなしで全件表示
  filterDeptIds?: string[];

  // options に存在しない selected の表示用（例: creators の wr_id -> label）
  externalLabelMap?: Map<string, string> | Record<string, string>;

  // 既定は後方互換のためラベル一致も同一選択として扱う。
  // 発行者など同名の別ユーザーを区別する場合は id を指定する。
  identityMode?: 'label' | 'id';

  // trueの間はプルダウンを開けない（例：先に別の絞り込み条件を選ばせたい場合）
  disabled?: boolean;
}

/** 未チェック／チェックのアイコン（indeterminate も未チェックと同じ見た目に固定） */
const outlineIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const filledIcon = <CheckBoxIcon fontSize="small" />;
const normalizeGroupLabel = (v: unknown): string =>
  String(v ?? '').normalize('NFKC').trim();
const isPastTeamGroupLabel = (label: string): boolean =>
  normalizeGroupLabel(label) === '過去チーム';
const invertedOutlineIcon = (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '2px',
      bgcolor: '#ffffff',
      border: '1px solid #1f3a93',
      boxSizing: 'border-box',
    }}
  />
);
const invertedFilledIcon = (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '2px',
      bgcolor: '#ffffff',
      border: '1px solid #1f3a93',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6,
        height: 10,
        borderRight: '2.5px solid #1f3a93',
        borderBottom: '2.5px solid #1f3a93',
        transform: 'rotate(45deg) translateY(-1px)',
        boxSizing: 'border-box',
      }}
    />
  </Box>
);
const invertedIndeterminateIcon = (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '2px',
      bgcolor: '#ffffff',
      border: '1px solid #1f3a93',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <RemoveIcon sx={{ fontSize: 16, color: '#1f3a93' }} />
  </Box>
);

// 全角スペースや余分な空白を吸収して比較する
const normalizeLabel = (s: string) => {
  return String(s ?? '')
    .replace(/\u3000/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

// 氏名検索では、全角/半角の揺れと姓・名の空白有無を同一として扱う。
const normalizeSearchText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .toLowerCase();

const uniq = (arr: string[]) => Array.from(new Set(arr.map((v) => String(v))));

type MenuListActions = {
  adjustStyleForScrollbar: (
    containerElement: HTMLElement,
    options: { direction: 'ltr' | 'rtl' }
  ) => void;
};

const VirtualizedMenuList = React.forwardRef<HTMLDivElement, any>(
  function VirtualizedMenuList(props, forwardedRef) {
    const {
      actions,
      autoFocus: _autoFocus,
      autoFocusItem: _autoFocusItem,
      children,
      className,
      disableListWrap: _disableListWrap,
      onScroll,
      ownerState: _ownerState,
      style,
      variant: _variant,
      virtualItemHeight = 48,
      virtualMaxVisibleItems = 6,
      virtualFixedRowHeight = false,
      ...other
    } = props;
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const [manualScrollOffset, setManualScrollOffset] = React.useState(0);
    const rows = React.useMemo(
      () =>
        React.Children.toArray(children).filter(
          (child): child is React.ReactElement<any> => React.isValidElement(child)
        ),
      [children]
    );
    const isVirtualRow = React.useCallback((child: React.ReactElement<any>) => {
      const rowClassName = String(child.props.className ?? '');
      return (
        rowClassName.split(/\s+/).includes('option-item') ||
        rowClassName.split(/\s+/).includes('option-group-header')
      );
    }, []);
    const fixedRows = React.useMemo(
      () => rows.filter((child) => !isVirtualRow(child)),
      [isVirtualRow, rows]
    );
    const virtualRows = React.useMemo(
      () => rows.filter(isVirtualRow),
      [isVirtualRow, rows]
    );
    const getItemKey = React.useCallback(
      (index: number) => virtualRows[index]?.key ?? index,
      [virtualRows]
    );
    const getEstimatedRowSize = React.useCallback(
      (index: number) => {
        const rowClassName = String(virtualRows[index]?.props.className ?? '');
        return rowClassName.includes('option-group-header')
          ? virtualItemHeight * 0.75
          : virtualItemHeight;
      },
      [virtualItemHeight, virtualRows]
    );
    const virtualizer = useVirtualizer({
      count: virtualRows.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: getEstimatedRowSize,
      getItemKey,
      initialRect: {
        height: virtualItemHeight * virtualMaxVisibleItems,
        width: 280,
      },
      overscan: 5,
    });
    const measuredVirtualRows = virtualizer.getVirtualItems();
    const estimatedStartIndex = Math.max(
      0,
      Math.floor(manualScrollOffset / virtualItemHeight) - 5
    );
    const measuredRowsCoverOffset =
      manualScrollOffset === 0 ||
      measuredVirtualRows.some(
        (row) =>
          row.index >= estimatedStartIndex &&
          row.index <= estimatedStartIndex + virtualMaxVisibleItems + 10
      );
    const visibleVirtualRows =
      measuredVirtualRows.length > 0 && measuredRowsCoverOffset
        ? measuredVirtualRows
        : virtualRows
            .slice(
              estimatedStartIndex,
              estimatedStartIndex + virtualMaxVisibleItems + 10
            )
            .map((_, offset) => {
              const index = estimatedStartIndex + offset;
              return {
              end: (index + 1) * virtualItemHeight,
              index,
              key: getItemKey(index),
              lane: 0,
              size: virtualItemHeight,
              start: index * virtualItemHeight,
              };
            });
    const estimatedVirtualTotalSize = virtualRows.reduce(
      (total, _row, index) => total + getEstimatedRowSize(index),
      0
    );
    const virtualTotalSize = Math.max(
      virtualizer.getTotalSize(),
      estimatedVirtualTotalSize
    );

    React.useImperativeHandle(
      actions as React.Ref<MenuListActions> | undefined,
      () => ({
        adjustStyleForScrollbar: () => undefined,
      }),
      []
    );

    return (
      <div
        {...other}
        ref={forwardedRef}
        className={className}
        style={{ ...style, paddingTop: 0, paddingBottom: 0 }}
      >
        <div className="virtualized-options-fixed">
          {fixedRows.map((child) =>
            React.cloneElement(child, {
              component: 'div',
            })
          )}
        </div>
        {virtualRows.length > 0 && (
          <div
            ref={scrollRef}
            data-testid="virtualized-options-scroll"
            onScroll={(event) => {
              setManualScrollOffset(event.currentTarget.scrollTop);
              onScroll?.(event);
            }}
            style={{
              maxHeight: virtualItemHeight * virtualMaxVisibleItems,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              position: 'relative',
            }}
          >
            <div
              style={{
                height: virtualTotalSize,
                position: 'relative',
                width: '100%',
              }}
            >
              {visibleVirtualRows.map((virtualRow) => {
                const child = virtualRows[virtualRow.index];
                return React.cloneElement(child, {
                  component: 'div',
                  'data-index': virtualRow.index,
                  ref: virtualFixedRowHeight
                    ? undefined
                    : virtualizer.measureElement,
                  style: {
                    ...(child.props.style ?? {}),
                    ...(virtualFixedRowHeight
                      ? {
                          height: virtualRow.size,
                          maxHeight: virtualRow.size,
                        }
                      : {}),
                    left: 0,
                    minHeight: virtualRow.size,
                    position: 'absolute',
                    top: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    width: '100%',
                  },
                });
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
);

const MultiSelectCheckbox: React.FC<MultiSelectCheckboxProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  onOpen,
  itemHeight = 48,
  maxVisibleItems = 6,
  maxRenderedOptions,
  maxSearchRenderedOptions = 100,
  initialRenderedOptions,
  renderedOptionsStep = 100,
  renderAllOptionsWhenLoaded = false,
  maxAutoRenderedOptions,
  loadedOptionCount,
  allOptionsLoaded = true,
  optionDisplayReady,
  remoteOptions = [],
  onFilterTextChange,
  searchLoading = false,
  searchResultsComplete = true,
  searchError = null,
  loadErrorMessage = null,
  notifyUnlistedSelected = false,
  unlistedSelectedIgnoreIds,
  onClearUnlistedSelected,
  searchLoadingText = '候補を検索中です…',
  showSearchLoadingMessage = true,
  virtualizeOptions = false,
  disableMenuTransition = false,
  fixedVirtualRowHeight = false,
  countUnit = '件',
  loading = false,
  loadingText = '候補を読み込み中です…',
  showLoadingMessage = true,
  filterDeptIds, // 自分の課
  externalLabelMap,
  identityMode = 'label',
  disabled = false,
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const checkboxColor = isDark ? '#38bdf8' : '#1f3a93';

  /** 入力フィルター */
  const [filterText, setFilterText] = React.useState('');
  const deferredFilterText = React.useDeferredValue(filterText);
  const filterInputRef = React.useRef<HTMLInputElement | null>(null);
  const filterComposingRef = React.useRef(false);

  const maximumRenderedOptions = React.useMemo(() => {
    if (maxRenderedOptions == null || !Number.isFinite(maxRenderedOptions)) {
      return null;
    }
    return Math.max(1, Math.floor(maxRenderedOptions));
  }, [maxRenderedOptions]);
  const maximumSearchRenderedOptions = React.useMemo(() => {
    if (!Number.isFinite(maxSearchRenderedOptions)) return 100;
    return Math.max(1, Math.floor(maxSearchRenderedOptions));
  }, [maxSearchRenderedOptions]);
  const maximumAutoRenderedOptions = React.useMemo(() => {
    if (maxAutoRenderedOptions == null || !Number.isFinite(maxAutoRenderedOptions)) {
      return null;
    }
    const normalized = Math.max(1, Math.floor(maxAutoRenderedOptions));
    return maximumRenderedOptions == null
      ? normalized
      : Math.min(normalized, maximumRenderedOptions);
  }, [maxAutoRenderedOptions, maximumRenderedOptions]);
  const initialOptionLimit = React.useMemo(() => {
    if (maximumRenderedOptions == null) return null;
    if (initialRenderedOptions == null || !Number.isFinite(initialRenderedOptions)) {
      return maximumRenderedOptions;
    }
    return Math.min(
      maximumRenderedOptions,
      Math.max(1, Math.floor(initialRenderedOptions))
    );
  }, [initialRenderedOptions, maximumRenderedOptions]);
  const [currentRenderedLimit, setCurrentRenderedLimit] = React.useState<number | null>(
    initialOptionLimit
  );

  React.useEffect(() => {
    setCurrentRenderedLimit(initialOptionLimit);
  }, [initialOptionLimit]);

  React.useEffect(() => {
    if (!allOptionsLoaded) {
      setCurrentRenderedLimit(initialOptionLimit);
    }
  }, [allOptionsLoaded, initialOptionLimit]);

  /** メニューを開いたら入力欄に即フォーカス */
  const handleMenuOpen = () => {
    // 完成済み候補は同期的に最終表示件数を使うため、開くたびに100件へ戻さない。
    if (!(optionDisplayReady === true && renderAllOptionsWhenLoaded)) {
      setCurrentRenderedLimit(initialOptionLimit);
    }
    onOpen?.();
    requestAnimationFrame(() => {
      filterInputRef.current?.focus();
      filterInputRef.current?.select();
    });
  };

  const hasFilterText = normalizeSearchText(deferredFilterText).length > 0;
  const allAvailableOptions = React.useMemo(() => {
    if (!hasFilterText || remoteOptions.length === 0) return options;
    const merged = new Map<string, Option>();
    options.forEach((option) => merged.set(String(option.id), option));
    remoteOptions.forEach((option) => merged.set(String(option.id), option));
    const values = Array.from(merged.values());
    if (identityMode !== 'id') return values;

    const counts = new Map<string, number>();
    values.forEach((option) => {
      const key = normalizeSearchText(option.value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return values.map((option) => {
      const key = normalizeSearchText(option.value);
      if (option.displayValue || (counts.get(key) ?? 0) < 2) return option;
      return {
        ...option,
        displayValue: `${option.value}（${option.id}）`,
      };
    });
  }, [hasFilterText, identityMode, options, remoteOptions]);

  /** 課IDフィルター（※入力フィルターより先に適用） */
  const deptFilteredOptions = React.useMemo(() => {
    if (!filterDeptIds || filterDeptIds.length === 0) {
      return allAvailableOptions;
    }

    return allAvailableOptions.filter((opt) => {
      // department_id が無いものは「不明」なので除外しない（安全側）
      if (!opt.department_id) return true;

      return filterDeptIds.includes(String(opt.department_id));
    });
  }, [allAvailableOptions, filterDeptIds]);

  const searchableOptions = React.useMemo(
    () =>
      deptFilteredOptions.map((option) => ({
        option,
        valueKey: normalizeSearchText(option.value),
        displayValueKey: normalizeSearchText(option.displayValue),
        groupKey: normalizeSearchText(option.groupLabel),
      })),
    [deptFilteredOptions]
  );

  /** 課IDフィルター → 入力フィルターの順で絞る */
  const filteredOptions = React.useMemo(() => {
    const q = normalizeSearchText(deferredFilterText);
    if (!q) return deptFilteredOptions;

    // 分類名(groupLabel)でヒットした場合は、その分類配下を全表示する
    const matchedGroupKeys = new Set<string>();
    searchableOptions.forEach(({ groupKey }) => {
      if (groupKey && groupKey.includes(q)) {
        matchedGroupKeys.add(groupKey);
      }
    });

    return searchableOptions
      .filter(
        ({ valueKey, displayValueKey, groupKey }) =>
          valueKey.includes(q) ||
          displayValueKey.includes(q) ||
          Boolean(groupKey && matchedGroupKeys.has(groupKey))
      )
      .map(({ option }) => option);
  }, [deferredFilterText, deptFilteredOptions, searchableOptions]);

  // ブラウザへ描画の合間を渡しながら500件ずつ候補を増やす。
  // 上限指定時は全データの取得を継続しつつ、DOMだけを指定件数で止める。
  React.useEffect(() => {
    if (
      optionDisplayReady !== undefined ||
      !renderAllOptionsWhenLoaded ||
      hasFilterText ||
      filteredOptions.length === 0 ||
      (maximumAutoRenderedOptions == null && !allOptionsLoaded)
    ) {
      return;
    }

    const current = currentRenderedLimit ?? initialOptionLimit ?? 0;
    const target = Math.min(
      filteredOptions.length,
      maximumAutoRenderedOptions ?? filteredOptions.length
    );
    if (current >= target) return;

    const frame = window.requestAnimationFrame(() => {
      setCurrentRenderedLimit((value) => {
        const base = value ?? initialOptionLimit ?? 0;
        return Math.min(target, base + 500);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    allOptionsLoaded,
    currentRenderedLimit,
    filteredOptions.length,
    hasFilterText,
    initialOptionLimit,
    maximumAutoRenderedOptions,
    optionDisplayReady,
    renderAllOptionsWhenLoaded,
  ]);

  const readyRenderedOptionLimit =
    !hasFilterText &&
    optionDisplayReady === true &&
    renderAllOptionsWhenLoaded
      ? Math.min(
          filteredOptions.length,
          maximumAutoRenderedOptions ?? filteredOptions.length
        )
      : null;
  const renderedOptionLimit = hasFilterText
    ? maximumRenderedOptions == null
      ? maximumSearchRenderedOptions
      : Math.min(maximumSearchRenderedOptions, maximumRenderedOptions)
    : readyRenderedOptionLimit ?? currentRenderedLimit;

  const isOptionListTruncated =
    renderedOptionLimit != null && filteredOptions.length > renderedOptionLimit;

  const showProgressiveLoadStatus =
    !hasFilterText &&
    initialRenderedOptions != null &&
    filteredOptions.length > 0 &&
    (loading || (renderAllOptionsWhenLoaded && allOptionsLoaded));

  const renderedOptions = React.useMemo(() => {
    if (renderedOptionLimit == null) return filteredOptions;
    return filteredOptions.slice(0, renderedOptionLimit);
  }, [filteredOptions, renderedOptionLimit]);
  const finalRenderedOptionCount = React.useMemo(() => {
    if (hasFilterText || !renderAllOptionsWhenLoaded) {
      return Math.min(
        filteredOptions.length,
        renderedOptionLimit ?? filteredOptions.length
      );
    }
    return Math.min(
      filteredOptions.length,
      maximumAutoRenderedOptions ?? filteredOptions.length
    );
  }, [
    filteredOptions.length,
    hasFilterText,
    maximumAutoRenderedOptions,
    renderedOptionLimit,
    renderAllOptionsWhenLoaded,
  ]);
  const optionRowsVisible =
    optionDisplayReady === undefined ||
    (optionDisplayReady &&
      renderedOptions.length >= finalRenderedOptionCount);
  const allRowsRendered = renderedOptions.length >= filteredOptions.length;
  const autoDisplayLimitReached = Boolean(
    maximumAutoRenderedOptions != null &&
      filteredOptions.length > maximumAutoRenderedOptions &&
      renderedOptions.length >= maximumAutoRenderedOptions
  );
  const searchResultLimitReached =
    hasFilterText &&
    !searchLoading &&
    !searchResultsComplete &&
    renderedOptionLimit != null &&
    renderedOptions.length >= renderedOptionLimit;
  const showOptionLimitStatus =
    optionRowsVisible &&
    (isOptionListTruncated ||
      showProgressiveLoadStatus ||
      searchResultLimitReached);
  const optionLimitMessage = React.useMemo(() => {
    if (hasFilterText) {
      if (isOptionListTruncated || searchResultLimitReached) {
        return `検索結果を${renderedOptions.length.toLocaleString()}${countUnit}まで表示しています。さらに絞り込んでください。`;
      }
      return `${filteredOptions.length.toLocaleString()}${countUnit}中${renderedOptions.length.toLocaleString()}${countUnit}を表示しています。`;
    }

    const total = Number(loadedOptionCount ?? options.length);
    if (
      renderAllOptionsWhenLoaded &&
      allOptionsLoaded &&
      (autoDisplayLimitReached || isOptionListTruncated)
    ) {
      return `${total.toLocaleString()}${countUnit}中${renderedOptions.length.toLocaleString()}${countUnit}を表示しています。表示されていない発行者は名前を入力して検索できます。`;
    }
    if (renderAllOptionsWhenLoaded && allOptionsLoaded && allRowsRendered) {
      return `${total.toLocaleString()}${countUnit}中${renderedOptions.length.toLocaleString()}${countUnit}を表示しています。`;
    }
    if (loading) {
      return `${total.toLocaleString()}${countUnit}取得済み・${renderedOptions.length.toLocaleString()}${countUnit}表示中。残りの発行者を読み込み中です。名前を入力すると未取得の発行者も検索できます。`;
    }
    return `${total.toLocaleString()}${countUnit}取得済み・${renderedOptions.length.toLocaleString()}${countUnit}表示中。続きはプルダウンを開き直すと再試行します。`;
  }, [
    allOptionsLoaded,
    allRowsRendered,
    autoDisplayLimitReached,
    countUnit,
    filteredOptions.length,
    hasFilterText,
    isOptionListTruncated,
    loadedOptionCount,
    loading,
    options.length,
    renderAllOptionsWhenLoaded,
    renderedOptions.length,
    searchResultLimitReached,
  ]);

  const groupedFilteredOptions = React.useMemo(() => {
    const groups: Array<{ label: string; options: Option[] }> = [];
    const index = new Map<string, number>();

    renderedOptions.forEach((opt) => {
      const label = String(opt.groupLabel ?? '').trim();
      const key = label || '__ungrouped__';
      if (!index.has(key)) {
        index.set(key, groups.length);
        groups.push({ label, options: [] });
      }
      groups[index.get(key) as number].options.push(opt);
    });

    groups.sort((a, b) => {
      const aPast = isPastTeamGroupLabel(a.label);
      const bPast = isPastTeamGroupLabel(b.label);
      if (aPast === bPast) return 0;
      return aPast ? 1 : -1;
    });

    return groups;
  }, [renderedOptions]);

  const hasGroupedOptions = React.useMemo(
    () => renderedOptions.some((o) => String(o.groupLabel ?? '').trim().length > 0),
    [renderedOptions]
  );

  // id -> value の引き当て（renderValue 用）
  const optionLabelMap = React.useMemo(() => {
    const map = new Map<string, string>();
    allAvailableOptions.forEach((o) => {
      map.set(String(o.id), String(o.displayValue ?? o.value ?? ''));
    });
    return map;
  }, [allAvailableOptions]);

  // options から一瞬消えたときでも表示ラベルを残す（表示が "条件なし" に落ちるのを防ぐ）
  const lastOptionLabelMapRef = React.useRef<Map<string, string>>(new Map());

  React.useEffect(() => {
    if (allAvailableOptions.length === 0) return;
    allAvailableOptions.forEach((o) => {
      lastOptionLabelMapRef.current.set(
        String(o.id),
        String(o.displayValue ?? o.value ?? '')
      );
    });
  }, [allAvailableOptions]);

  // ★外部ラベル（wr_id -> label など）
  const getExternalLabel = React.useCallback(
    (id: string): string => {
      if (!externalLabelMap) return '';
      if (externalLabelMap instanceof Map) {
        return String(externalLabelMap.get(String(id)) ?? '');
      }
      return String(
        (externalLabelMap as Record<string, string>)[String(id)] ?? ''
      );
    },
    [externalLabelMap]
  );

  // selectedId から「表示用ラベル」を引く（存在しないIDでも external で引ける）
  const getLabelForSelectedId = React.useCallback(
    (id: string): string => {
      return (
        optionLabelMap.get(String(id)) ??
        lastOptionLabelMapRef.current.get(String(id)) ??
        getExternalLabel(String(id)) ??
        ''
      );
    },
    [optionLabelMap, getExternalLabel]
  );

  // selectedValues（wr_id等）から「選択済みラベル集合」を作る
  const selectedLabelSet = React.useMemo(() => {
    const set = new Set<string>();
    (selectedValues ?? []).forEach((id) => {
      const lb = normalizeLabel(getLabelForSelectedId(String(id)));
      if (lb) set.add(lb);
    });
    return set;
  }, [selectedValues, getLabelForSelectedId]);

  const selectedIdSet = React.useMemo(
    () => new Set((selectedValues ?? []).map((v) => String(v))),
    [selectedValues]
  );

  // 選択中だが、取得完了済みの現在候補に存在しないID数（URL指定などで起こる）。
  const unlistedSelectedCount = React.useMemo(() => {
    if (!notifyUnlistedSelected || !allOptionsLoaded || loading) return 0;
    const ignored = new Set(
      (unlistedSelectedIgnoreIds ?? []).map((id) => String(id).trim().toLocaleLowerCase())
    );
    let count = 0;
    selectedIdSet.forEach((id) => {
      if (ignored.has(id.trim().toLocaleLowerCase())) return;
      if (!optionLabelMap.has(id) && !getExternalLabel(id)) count += 1;
    });
    return count;
  }, [
    notifyUnlistedSelected,
    allOptionsLoaded,
    loading,
    selectedIdSet,
    optionLabelMap,
    getExternalLabel,
    unlistedSelectedIgnoreIds,
  ]);

  // 「この option がチェック扱いか？」（id一致 または label一致）
  const isOptionChecked = React.useCallback(
    (opt: Option) => {
      if (selectedIdSet.has(String(opt.id))) return true;
      if (identityMode === 'id') return false;

      const optLabel = normalizeLabel(String(opt.value ?? ''));
      if (!optLabel) return false;

      return selectedLabelSet.has(optLabel);
    },
    [selectedIdSet, selectedLabelSet, identityMode]
  );

  /** 絞り込み結果全体に対する全選択状態（省略表示中は全選択不可） */
  const resultSetComplete = hasFilterText
    ? searchResultsComplete && !searchLoading
    : allOptionsLoaded;
  const canToggleAll =
    optionRowsVisible &&
    filteredOptions.length > 0 &&
    filteredOptions.length <= 11000 &&
    !isOptionListTruncated &&
    resultSetComplete;
  const isAllSelected =
    canToggleAll &&
    filteredOptions.every((o) => isOptionChecked(o));

  /** 表示用「全て選択」：手打ち絞り込みの結果ではなく、絞り込み前の全候補が基準 */
  const isEveryOptionSelected = React.useMemo(() => {
    const baseOptions =
      !filterDeptIds || filterDeptIds.length === 0
        ? options
        : options.filter(
            (opt) =>
              !opt.department_id ||
              filterDeptIds.includes(String(opt.department_id))
          );
    return (
      allOptionsLoaded &&
      baseOptions.length > 0 &&
      baseOptions.every((o) => isOptionChecked(o))
    );
  }, [allOptionsLoaded, filterDeptIds, isOptionChecked, options]);

  /** チェック後に入力欄へ必ず戻す */
  const refocusFilterSoon = () => {
    requestAnimationFrame(() => {
      const input = filterInputRef.current;
      const menuList = input?.closest('.MuiMenu-list') as HTMLElement | null;
      const prevScrollTop = menuList?.scrollTop ?? null;

      if (input) {
        try {
          input.focus({ preventScroll: true } as FocusOptions);
        } catch {
          input.focus();
        }
      }
      // 末尾にキャレットを置く
      if (input) {
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }

      // focus に伴うメニュー内スクロールのジャンプを戻す
      if (menuList && prevScrollTop != null) {
        requestAnimationFrame(() => {
          menuList.scrollTop = prevScrollTop;
        });
      }
    });
  };

  // label一致で選ばれている可能性もあるので、解除時は「同じlabelのselected」をまとめて落とす
  const removeByOptionLabel = React.useCallback(
    (base: string[], opt: Option) => {
      if (identityMode === 'id') {
        return base.filter((v) => String(v) !== String(opt.id));
      }

      const targetLabel = normalizeLabel(String(opt.value ?? ''));
      if (!targetLabel) {
        return base.filter((v) => String(v) !== String(opt.id));
      }

      return base.filter((v) => {
        const id = String(v);

        // まず id 直一致は落とす
        if (id === String(opt.id)) return false;

        // ラベル一致のものも落とす（wr_id が別でも同一人物扱い）
        const lb = normalizeLabel(getLabelForSelectedId(id));
        return lb !== targetLabel;
      });
    },
    [getLabelForSelectedId, identityMode]
  );

  // 追加は options 側の id（= 今表示してる代表 wr_id）を入れる
  const addOptionId = React.useCallback((base: string[], opt: Option) => {
    return uniq([...base, String(opt.id)]);
  }, []);

  const getGroupCheckState = React.useCallback(
    (groupOptions: Option[]) => {
      if (groupOptions.length === 0) {
        return { checked: false, indeterminate: false };
      }
      const checkedCount = groupOptions.reduce(
        (acc, opt) => acc + (isOptionChecked(opt) ? 1 : 0),
        0
      );
      return {
        checked: checkedCount === groupOptions.length,
        indeterminate: checkedCount > 0 && checkedCount < groupOptions.length,
      };
    },
    [isOptionChecked]
  );

  const toggleGroupOptions = React.useCallback(
    (groupOptions: Option[]) => {
      if (!Array.isArray(groupOptions) || groupOptions.length === 0) return;

      const { checked } = getGroupCheckState(groupOptions);
      let next = (selectedValues ?? []).map(String);

      if (checked) {
        groupOptions.forEach((opt) => {
          next = removeByOptionLabel(next, opt);
        });
      } else {
        groupOptions.forEach((opt) => {
          next = addOptionId(next, opt);
        });
      }

      onChange(uniq(next));
      refocusFilterSoon();
    },
    [selectedValues, getGroupCheckState, removeByOptionLabel, addOptionId, onChange]
  );

  // 「全て選択/解除」も label一致を考慮
  const toggleAllShown = React.useCallback(() => {
    if (!canToggleAll) return;

    const base = (selectedValues ?? []).map(String);

    if (isAllSelected) {
      if (identityMode === 'id') {
        const shownIds = new Set(filteredOptions.map((o) => String(o.id)));
        onChange(uniq(base.filter((id) => !shownIds.has(String(id)))));
        refocusFilterSoon();
        return;
      }

      // 全解除：表示中候補の label に一致する selected もまとめて外す
      const shownLabels = new Set(
        filteredOptions
          .map((o) => normalizeLabel(String(o.value ?? '')))
          .filter((x) => x.length > 0)
      );

      const next = base.filter((id) => {
        const inShownId = filteredOptions.some(
          (o) => String(o.id) === String(id)
        );
        if (inShownId) return false;

        const lb = normalizeLabel(getLabelForSelectedId(id));
        if (lb && shownLabels.has(lb)) return false;

        return true;
      });

      onChange(uniq(next));
      refocusFilterSoon();
      return;
    }

    // 全選択：表示中候補の id を追加（既存は保持）
    const next = uniq([...base, ...filteredOptions.map((o) => String(o.id))]);
    onChange(next);
    refocusFilterSoon();
  }, [
    selectedValues,
    canToggleAll,
    isAllSelected,
    filteredOptions,
    getLabelForSelectedId,
    onChange,
    identityMode,
  ]);

  /** Select の変更ハンドラ（行クリック用）
   * - MUIが作る value の差分を見て、
   *   OFF のときは label一致の selected もまとめて外す
   */
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value as string[];
    const value = (Array.isArray(raw) ? raw : []).map(String);

    if (value.includes('all')) {
      toggleAllShown();
      return;
    }

    const optionIdSet = new Set(options.map((o) => String(o.id)));

    // 現在の「実質選択（id or label）」を options id として表現
    const effectiveSelectedOptionIdSet = new Set<string>();
    (selectedValues ?? []).forEach((v) => {
      const id = String(v);
      if (optionIdSet.has(id)) effectiveSelectedOptionIdSet.add(id);
    });
    if (identityMode !== 'id') {
      options.forEach((o) => {
        const lb = normalizeLabel(String(o.value ?? ''));
        if (lb && selectedLabelSet.has(lb)) {
          effectiveSelectedOptionIdSet.add(String(o.id));
        }
      });
    }

    const nextMuiSet = new Set(value.filter((v) => v !== 'all'));

    const toggledOn = Array.from(nextMuiSet).filter(
      (id) => !effectiveSelectedOptionIdSet.has(String(id))
    );
    const toggledOff = Array.from(effectiveSelectedOptionIdSet).filter(
      (id) => !nextMuiSet.has(String(id))
    );

    let next = (selectedValues ?? []).map(String);

    toggledOff.forEach((offId) => {
      const opt = options.find((o) => String(o.id) === String(offId));
      if (!opt) {
        next = next.filter((v) => String(v) !== String(offId));
        return;
      }
      next = removeByOptionLabel(next, opt);
    });

    toggledOn.forEach((onId) => {
      const opt = options.find((o) => String(o.id) === String(onId));
      if (!opt) return;
      next = addOptionId(next, opt);
    });

    onChange(uniq(next));
    refocusFilterSoon();
  };

  /** 選択表示（未選択→「条件なし」、全選択→「全て選択」） */
  const renderValue = (selected: any) => {
    const selectedIds = Array.isArray(selected)
      ? (selected as any[]).map((v) => String(v))
      : selected
        ? [String(selected)]
        : [];

    if (!selectedIds.length) return '条件なし';

    if (isEveryOptionSelected) {
      return '全て選択';
    }

    // label mode は同一人物の旧IDをまとめる。id mode は同名別ユーザーも保持する。
    const labels = selectedIds
      .map((id) => getLabelForSelectedId(String(id)))
      .map((s) => String(s).trim())
      .filter((s) => s.length > 0);

    if (identityMode === 'id') {
      return labels.length > 0 ? labels.join('、') : `選択済み(${selectedIds.length})`;
    }

    const uniqLabels: string[] = [];
    const seen = new Set<string>();
    labels.forEach((lb) => {
      const key = normalizeLabel(lb);
      if (!key) return;
      if (seen.has(key)) return;
      seen.add(key);
      uniqLabels.push(lb);
    });

    if (uniqLabels.length === 0) {
      return `選択済み(${selectedIds.length})`;
    }

    return uniqLabels.join('、');
  };

  /** フィルタ行のマウスダウンは「入力欄以外の時だけ」既定動作を止める */
  const handleFilterRowMouseDown: React.MouseEventHandler<HTMLLIElement> = (e) => {
    const target = e.target as HTMLElement;
    const isInput =
      target.tagName.toLowerCase() === 'input' || !!target.closest('input');

    if (!isInput) {
      e.preventDefault();
    }
    e.stopPropagation();
  };

  const handleMenuScroll: React.UIEventHandler<HTMLElement> = (event) => {
    if (hasFilterText || maximumRenderedOptions == null) return;
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight > itemHeight) {
      return;
    }
    setCurrentRenderedLimit((current) => {
      const base = current ?? initialOptionLimit ?? maximumRenderedOptions;
      return Math.min(
        maximumRenderedOptions,
        base + Math.max(1, Math.floor(renderedOptionsStep))
      );
    });
  };

  return (
    <div className="form-field" id="select-check">
      <FormControl fullWidth>
        <label className="label-check">{label}</label>
        <Select
          multiple
          displayEmpty
          disabled={disabled}
          value={selectedValues}
          onChange={handleChange}
          onOpen={handleMenuOpen}
          input={<OutlinedInput />}
          renderValue={renderValue}
          MenuProps={{
            transitionDuration: disableMenuTransition ? 0 : undefined,
            ...(virtualizeOptions
              ? {
                  slots: {
                    list: VirtualizedMenuList as React.ElementType,
                  },
                }
              : {}),
            MenuListProps: {
              autoFocusItem: false,
              onScroll: handleMenuScroll,
              ...(virtualizeOptions
                ? {
                    virtualItemHeight: itemHeight,
                    virtualMaxVisibleItems: maxVisibleItems,
                    virtualFixedRowHeight: fixedVirtualRowHeight,
                  }
                : {}),
            } as any,
            PaperProps: {
              style: {
                width: 280,
                ...(virtualizeOptions
                  ? { overflow: 'hidden' }
                  : {
                      maxHeight:
                        itemHeight * maxVisibleItems + itemHeight / 2,
                    }),
              },
            },
          }}
          onKeyDownCapture={(e) => {
            if ((e.target as HTMLElement).tagName.toLowerCase() === 'input') {
              e.stopPropagation();
            }
          }}
          sx={{
            '& .MuiSelect-select': {
              fontSize: '0.875rem',
            },
          }}
        >
          {/* 入力フィルター行（選択不可の行） */}
          <MenuItem
            disableRipple
            disableTouchRipple
            className="filter-row"
            onMouseDown={handleFilterRowMouseDown}
            onClick={(e) => e.stopPropagation()}
            sx={{ cursor: 'default' }}
          >
            <TextField
              inputRef={filterInputRef}
              placeholder="入力で絞り込み"
              size="small"
              fullWidth
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                if (!filterComposingRef.current) {
                  onFilterTextChange?.(e.target.value);
                }
              }}
              onCompositionStart={() => {
                filterComposingRef.current = true;
              }}
              onCompositionEnd={(e) => {
                filterComposingRef.current = false;
                const nextValue = (e.target as HTMLInputElement).value;
                setFilterText(nextValue);
                onFilterTextChange?.(nextValue);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).select();
                  e.stopPropagation();
                }
              }}
            />
          </MenuItem>

          {((loading && showLoadingMessage) ||
            (searchLoading && showSearchLoadingMessage)) && (
            <ListSubheader
              component="div"
              disableSticky
              aria-live="polite"
              data-testid="options-loading-message"
              sx={{
                px: 2,
                py: 1,
                lineHeight: 1.5,
                whiteSpace: 'normal',
                color: 'primary.main',
                fontSize: '0.75rem',
              }}
            >
              {searchLoading && showSearchLoadingMessage
                ? searchLoadingText
                : loadingText}
            </ListSubheader>
          )}

          {searchError && hasFilterText && !searchLoading && (
            <ListSubheader
              component="div"
              disableSticky
              aria-live="polite"
              data-testid="options-search-error"
              sx={{
                px: 2,
                py: 1,
                lineHeight: 1.5,
                whiteSpace: 'normal',
                color: 'error.main',
                fontSize: '0.75rem',
              }}
            >
              {searchError}
            </ListSubheader>
          )}

          {loadErrorMessage && !loading && (
            <ListSubheader
              component="div"
              disableSticky
              aria-live="polite"
              data-testid="options-load-error"
              sx={{
                px: 2,
                py: 1,
                lineHeight: 1.5,
                whiteSpace: 'normal',
                color: 'error.main',
                fontSize: '0.75rem',
              }}
            >
              {loadErrorMessage}
            </ListSubheader>
          )}

          {!loadErrorMessage && unlistedSelectedCount > 0 && (
            <ListSubheader
              component="div"
              disableSticky
              aria-live="polite"
              data-testid="unlisted-selected-message"
              sx={{
                px: 2,
                py: 1,
                lineHeight: 1.5,
                whiteSpace: 'normal',
                color: 'warning.main',
                fontSize: '0.75rem',
              }}
            >
              選択中のうち{unlistedSelectedCount}
              {countUnit}は、現在の候補にありません（課・チーム・期間・公開範囲の条件による可能性があります）。
              {onClearUnlistedSelected && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    data-testid="clear-unlisted-selected-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearUnlistedSelected();
                    }}
                    // メニューを閉じたり、入力欄からフォーカスを奪わないようにする
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    候補に無い選択を解除
                  </Button>
                </Box>
              )}
            </ListSubheader>
          )}

          {showOptionLimitStatus && (
            <ListSubheader
              component="div"
              disableSticky
              aria-live="polite"
              data-testid="option-limit-message"
              sx={{
                px: 2,
                py: 1,
                lineHeight: 1.5,
                whiteSpace: 'normal',
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {optionLimitMessage}
            </ListSubheader>
          )}

          {/* 全て選択／解除（label一致も含む） */}
          <MenuItem
            value="all"
            className="all-toggle"
            disabled={!canToggleAll}
          >
            <Checkbox
              icon={outlineIcon}
              checkedIcon={filledIcon}
              indeterminateIcon={outlineIcon}
              checked={isAllSelected}
              indeterminate={false}
              disabled={!canToggleAll}
              sx={{ mr: 1 }}
              slotProps={{
                input: {
                  'aria-checked': isAllSelected ? 'true' : 'false',
                  'aria-label': '全て選択',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!canToggleAll) return;
                toggleAllShown();
              }}
            />
            <ListItemText
              primary={isAllSelected ? '全て解除' : '全て選択'}
              primaryTypographyProps={{ sx: { fontSize: '0.875rem' } }}
            />
          </MenuItem>

          {optionRowsVisible &&
            filteredOptions.length === 0 &&
            !loading &&
            !searchLoading &&
            !loadErrorMessage && (
            <MenuItem disabled data-testid="no-options-message">
              <ListItemText
                primary="該当する候補はありません"
                primaryTypographyProps={{ sx: { fontSize: '0.875rem' } }}
              />
            </MenuItem>
          )}

          {/* 個別選択：id一致だけでなく、label一致でも */}
          {optionRowsVisible && groupedFilteredOptions.flatMap((group, gi) => {
            const groupState = getGroupCheckState(group.options);
            const isPastTeamGroup = isPastTeamGroupLabel(group.label);
            const groupBgColor = isPastTeamGroup ? (isDark ? '#475569' : '#9e9e9e') : '#1f3a93';
            const groupHoverBgColor = isPastTeamGroup ? (isDark ? '#64748b' : '#8a8a8a') : '#183075';
            const elements: React.ReactNode[] = [];

            if (hasGroupedOptions && group.label) {
              elements.push(
                <ListSubheader
                  key={`group-${group.label || 'ungrouped'}-${gi}`}
                  className="option-group-header"
                  disableSticky
                  component="div"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroupOptions(group.options);
                  }}
                  sx={{
                    cursor: 'pointer',
                    minHeight: `${itemHeight * 0.75}px`,
                    height: `${itemHeight * 0.75}px`,
                    ...(fixedVirtualRowHeight
                      ? {
                          maxHeight: `${itemHeight * 0.75}px`,
                          overflow: 'hidden',
                        }
                      : {}),
                    py: 0,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: groupBgColor,
                    color: '#ffffff',
                    '&:hover': { bgcolor: groupHoverBgColor },
                  }}
                >
                  <Checkbox
                    icon={invertedOutlineIcon}
                    checkedIcon={invertedFilledIcon}
                    indeterminateIcon={invertedIndeterminateIcon}
                    checked={groupState.checked}
                    indeterminate={groupState.indeterminate}
                    sx={{
                      mr: 1,
                      py: 0,
                      color: checkboxColor,
                      '&.Mui-checked': { color: checkboxColor },
                      '&.MuiCheckbox-indeterminate': { color: checkboxColor },
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupOptions(group.options);
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      lineHeight: 'inherit',
                      color: 'inherit',
                      fontWeight: 600,
                      fontSize: '0.6875rem',
                    }}
                  >
                    {group.label}
                  </Box>
                </ListSubheader>
              );
            }

            group.options.forEach((opt) => {
              const checked = isOptionChecked(opt);

              elements.push(
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                  className="option-item"
                  title={
                    fixedVirtualRowHeight
                      ? String(opt.displayValue ?? opt.value)
                      : undefined
                  }
                  sx={{
                    whiteSpace: fixedVirtualRowHeight ? 'nowrap' : 'normal',
                    alignItems: 'center',
                    ...(fixedVirtualRowHeight
                      ? {
                          height: `${itemHeight}px`,
                          minHeight: `${itemHeight}px`,
                          maxHeight: `${itemHeight}px`,
                          overflow: 'hidden',
                        }
                      : {}),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    let next = (selectedValues ?? []).map(String);

                    if (checked) {
                      next = removeByOptionLabel(next, opt);
                    } else {
                      next = addOptionId(next, opt);
                    }

                    onChange(uniq(next));
                    refocusFilterSoon();
                  }}
                >
                  <Checkbox
                    icon={outlineIcon}
                    checkedIcon={filledIcon}
                    checked={checked}
                    sx={{ mr: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();

                      let next = (selectedValues ?? []).map(String);

                      if (checked) {
                        next = removeByOptionLabel(next, opt);
                      } else {
                        next = addOptionId(next, opt);
                      }

                      onChange(uniq(next));
                      refocusFilterSoon();
                    }}
                  />
                  {opt.color && (
                    <Box
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: opt.color,
                        border: '1px solid rgba(0,0,0,0.18)',
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <ListItemText
                    sx={{ my: 0 }}
                    primary={opt.displayValue ?? opt.value}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.875rem',
                        whiteSpace: fixedVirtualRowHeight ? 'nowrap' : 'normal',
                        wordBreak: fixedVirtualRowHeight ? 'normal' : 'break-word',
                        overflowWrap: fixedVirtualRowHeight ? 'normal' : 'anywhere',
                        ...(fixedVirtualRowHeight
                          ? {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }
                          : {}),
                      },
                    }}
                  />
                </MenuItem>
              );
            });

            return elements;
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default MultiSelectCheckbox;
